// ============================================================================
// HrHeadcount.jsx
// Two dashboards behind one page, because they answer two different questions
// off the same workforce and are constantly confused for one another, plus a
// month-by-month table of positions against budget:
//
//   Headcount    where does a person SIT — one head, one cost centre, whole
//                numbers, sourced from the roster's own Cost Center column.
//   Allocations  where does a person's COST land — fractional, sourced from
//                the Workday costing worktag, and one person can span several
//                cost centres.
//
// They do not tie, by design. 100 Corporate carries heads whose cost is
// charged out to the departments they lead; 110 IT carries cost with barely
// any heads. The reconciliation strip explains that difference in place
// rather than leaving it to be discovered and reported as a bug.
// ============================================================================

import { useState } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Scale, ClipboardList, AlertTriangle, Info } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { c, t, r, card } from '../theme';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import HeadcountTrend from '../components/HrHeadcount/HeadcountTrend';
import CostCenterTrend from '../components/HrHeadcount/CostCenterTrend';
import AllocationsTab from '../components/HrHeadcount/AllocationsTab';
import HeadcountTab from '../components/HrHeadcount/HeadcountTab';
import PositionsTab from '../components/HrHeadcount/PositionsTab';

const n2 = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n0 = (v) => Number(v ?? 0).toLocaleString();

const TABS = [
  { key: 'headcount',   label: 'Headcount by Cost Center', icon: Users,
    blurb: 'Where each person sits. One person counts once. Terminated workers are excluded; workers on leave are counted.' },
  { key: 'allocations', label: 'Headcount Allocations',    icon: Scale,
    blurb: 'Where each person’s cost lands. A person split across cost centers contributes a fraction to each, so totals are decimals.' },
  { key: 'positions',   label: 'Positions vs Budget',      icon: ClipboardList,
    blurb: 'Current employees in each position for the month, from that month’s last roster snapshot. Terminated workers are excluded; workers on leave are counted.' },
];

export default function HrHeadcount() {
  // Three different grants reach this page and they do not all reach both tabs.
  // Allocations is gated outright by can_view_hr_allocations; headcount also
  // admits a facility assignment, which is how every DVP arrives here. Drawing
  // both tabs regardless meant a DVP could click Allocations and be handed a
  // 403 from a tab we drew for them -- the backend was right and the page was
  // writing a cheque it could not cash.
  //
  // Mirrors the server: getHrCcAccess/buildCcFilter for headcount,
  // requireHrAllocationsAccess for the six allocation routes. If those change,
  // change these with them.
  const { user, isAdmin, hasOvertimeAccess } = useAuth();
  // Note the asymmetry, which is the server's and not an oversight here:
  // requireHrAllocationsAccess bypasses for super_admin ONLY, while headcount's
  // UNRESTRICTED_ROLES admits admin as well. isAdmin covers both roles, so using
  // it for allocations would draw the tab for an admin the server then 403s.
  const canSeeAllocations = user?.role === 'super_admin' || !!user?.canViewHrAllocations;
  const canSeeHeadcount   = isAdmin || !!user?.canViewHrHeadcount || hasOvertimeAccess;
  // Positions reads the roster and is scoped by the same getHrCcAccess, so it
  // follows headcount.
  const tabs = TABS.filter(({ key }) => (key === 'allocations' ? canSeeAllocations : canSeeHeadcount));

  // Someone holding only can_view_hr_allocations has no headcount tab to open,
  // so the default cannot be a constant.
  const [tab, setTab] = useState(canSeeHeadcount ? 'headcount' : 'allocations');
  // Held here rather than in the table, because the movement table sits above
  // it and needs to open a row it does not own.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const revealCostCenter = (id) => {
    setExpanded((prev) => new Set(prev).add(id));
    // Let the row render before scrolling to it.
    requestAnimationFrame(() => {
      document.getElementById(`cc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  // The reconciliation strip's own disclosure — who, not just how many — plus
  // the stand-in-for-Workday remediation (migration 112): which row (if any)
  // has its "add to roster" form open, and that form's own two fields.
  const [showAllocGap, setShowAllocGap] = useState(false);
  const [addingGapId, setAddingGapId] = useState(null);
  const [gapCostCenter, setGapCostCenter] = useState('');
  const [gapReason, setGapReason] = useState('');
  const qc = useQueryClient();
  const addToRoster = useMutation({
    mutationFn: ({ employee_id, worker_name }) => api.post('/hr-headcount/manual-roster-additions', {
      employee_id, worker_name, raw_cost_center: gapCostCenter, reason: gapReason,
    }),
    onSuccess: () => {
      setAddingGapId(null); setGapCostCenter(''); setGapReason('');
      qc.invalidateQueries({ queryKey: ['hr-headcount'] });
      qc.invalidateQueries({ queryKey: ['hr-reconciliation'] });
    },
  });

  // Dismissing is the "I recognize this one, it is not a real gap" action —
  // acknowledges noise (most often a recent termination Workday's costing
  // extract has not caught up to yet) without creating a roster row or
  // touching headcount at all, unlike addToRoster above.
  const [dismissingGapId, setDismissingGapId] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [showDismissed, setShowDismissed] = useState(false);
  const dismissals = useQuery({
    queryKey: ['hr-reconciliation-dismissals'],
    queryFn: () => api.get('/hr-headcount/reconciliation/dismissals'),
    enabled: canSeeAllocations && showDismissed,
  });
  const dismissGap = useMutation({
    mutationFn: ({ employee_id }) => api.post('/hr-headcount/reconciliation/dismissals', { employee_id, reason: dismissReason }),
    onSuccess: () => {
      setDismissingGapId(null); setDismissReason('');
      qc.invalidateQueries({ queryKey: ['hr-reconciliation'] });
      qc.invalidateQueries({ queryKey: ['hr-reconciliation-dismissals'] });
    },
  });
  const undoDismiss = useMutation({
    mutationFn: (employeeId) => api.post(`/hr-headcount/reconciliation/dismissals/${employeeId}/undo`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-reconciliation'] });
      qc.invalidateQueries({ queryKey: ['hr-reconciliation-dismissals'] });
    },
  });

  // Held here because the query needs the month, and so a filter survives a
  // trip to another tab. An empty month asks the server for the latest.
  const [positionMonth, setPositionMonth] = useState('');
  const [positionCostCenter, setPositionCostCenter] = useState('');

  const headcount = useQuery({
    queryKey: ['hr-headcount'],
    queryFn: () => api.get('/hr-headcount/headcount'),
    enabled: tab === 'headcount',
  });
  const allocations = useQuery({
    queryKey: ['hr-allocations'],
    queryFn: () => api.get('/hr-headcount/allocations'),
    enabled: tab === 'allocations',
  });
  const positions = useQuery({
    queryKey: ['hr-positions', positionMonth],
    queryFn: () => api.get('/hr-headcount/positions', { params: positionMonth ? { month: positionMonth } : {} }),
    enabled: tab === 'positions',
    // The month dropdown lives in this data. Without this it unmounts on every
    // change while the next month loads.
    placeholderData: keepPreviousData,
  });
  const recon = useQuery({
    queryKey: ['hr-reconciliation'],
    queryFn: () => api.get('/hr-headcount/reconciliation'),
    enabled: canSeeAllocations,
    retry: false,
  });

  const active = { headcount, allocations, positions }[tab];
  const meta = active.data?.snapshot;
  const tabDef = tabs.find((x) => x.key === tab);

  return (
    <div>
      <PageHeader title="HR Headcount" subtitle="Workday roster and costing allocations" />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: `1px solid ${c.border}` }}>
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer',
              ...t.label,
              color: tab === key ? c.brand : c.textMuted,
              borderBottom: tab === key ? `2px solid ${c.brand}` : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div style={{ ...t.bodySm, color: c.textMuted, marginBottom: 12 }}>{tabDef.blurb}</div>

      {/* Snapshot provenance — neither export carries a date, so say plainly
          which file this is and whether it was flagged incomplete. */}
      {meta && (
        <div style={{ ...t.bodySm, color: c.textMuted, marginBottom: 12 }}>
          Snapshot {meta.snapshot_date?.slice(0, 10) || '—'} ·{' '}
          {meta.roster_file_name || meta.allocation_file_name}
          {meta.is_provisional && (
            <span style={{ marginLeft: 8, padding: '1px 7px', borderRadius: r.pill,
                           background: c.warningTint, color: c.warningText, ...t.micro }}>
              provisional
            </span>
          )}
        </div>
      )}

      {/* Reconciliation — shown on both dashboards so the difference is never a
          surprise arrived at by subtraction. Not on Positions: these figures
          are the current snapshot, and that tab can be showing any month. */}
      {recon.data && tab !== 'positions' && (
        <div style={{ ...card.base, padding: '12px 16px', marginBottom: 16,
                      background: c.infoTint, borderColor: c.border }}>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'center' }}>
            <Info size={15} color={c.info} />
            <span style={{ ...t.bodySm, color: c.textStrong }}>
              <strong>{n0(recon.data.heads)}</strong> heads on the roster
            </span>
            <span style={{ ...t.bodySm, color: c.textStrong }}>
              <strong>{n2(recon.data.allocated_heads)}</strong> allocated
            </span>
            <span style={{ ...t.bodySm, color: c.textMuted }}>
              {n0(recon.data.heads_without_allocation)} charge to their home cost center
            </span>
            {recon.data.allocated_not_on_roster > 0 && (
              <button
                onClick={() => setShowAllocGap((v) => !v)}
                style={{ ...t.bodySm, color: c.warningText, display: 'flex', alignItems: 'center', gap: 5,
                         background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
              >
                <AlertTriangle size={14} />
                {n0(recon.data.allocated_not_on_roster)} allocated but missing from the roster — headcount is understated
              </button>
            )}
          </div>
          {showAllocGap && recon.data.allocated_not_on_roster > 0 && (
            <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
              {recon.data.allocated_not_on_roster_detail.map((w) => (
                <li key={w.employee_id} style={{ ...t.bodySm, color: c.textStrong, marginBottom: 4 }}>
                  {w.worker_name} ({w.employee_id})
                  {user?.role === 'super_admin' && addingGapId !== w.employee_id && dismissingGapId !== w.employee_id && (
                    <button
                      onClick={() => { setAddingGapId(w.employee_id); setGapCostCenter(''); setGapReason(''); }}
                      style={{ marginLeft: 10, ...t.micro, color: c.info, background: 'none', border: 'none',
                               padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                    >add to roster</button>
                  )}
                  {addingGapId !== w.employee_id && dismissingGapId !== w.employee_id && (
                    <button
                      onClick={() => { setDismissingGapId(w.employee_id); setDismissReason(''); }}
                      title="Not a real gap — e.g. a recent termination Workday's costing-allocations extract has not caught up to yet. Does not change headcount."
                      style={{ marginLeft: 10, ...t.micro, color: c.textMuted, background: 'none', border: 'none',
                               padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                    >dismiss</button>
                  )}
                  {dismissingGapId === w.employee_id && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input
                        placeholder="Reason (e.g. termed 11/1, required)"
                        value={dismissReason} onChange={(e) => setDismissReason(e.target.value)}
                        style={{ padding: '4px 6px', border: `1px solid ${c.border}`, borderRadius: r.md, ...t.bodySm, width: 260 }}
                      />
                      <button
                        disabled={!dismissReason || dismissGap.isPending}
                        onClick={() => dismissGap.mutate({ employee_id: w.employee_id })}
                        style={{ ...t.bodySm, color: c.info, background: 'none', border: `1px solid ${c.border}`,
                                 borderRadius: r.md, padding: '4px 8px', cursor: 'pointer' }}
                      >Dismiss</button>
                      <button
                        onClick={() => setDismissingGapId(null)}
                        style={{ ...t.bodySm, color: c.textMuted, background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer' }}
                      >Cancel</button>
                    </div>
                  )}
                  {dismissGap.isError && dismissingGapId === w.employee_id && (
                    <div style={{ ...t.micro, color: c.dangerText, marginTop: 2 }}>
                      {dismissGap.error?.message || 'Could not dismiss this worker.'}
                    </div>
                  )}
                  {addingGapId === w.employee_id && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input
                        placeholder="Cost center (e.g. 200 Salt Lake)"
                        value={gapCostCenter} onChange={(e) => setGapCostCenter(e.target.value)}
                        style={{ padding: '4px 6px', border: `1px solid ${c.border}`, borderRadius: r.md, ...t.bodySm, width: 200 }}
                      />
                      <input
                        placeholder="Reason (required)"
                        value={gapReason} onChange={(e) => setGapReason(e.target.value)}
                        style={{ padding: '4px 6px', border: `1px solid ${c.border}`, borderRadius: r.md, ...t.bodySm, width: 220 }}
                      />
                      <button
                        disabled={!gapCostCenter || !gapReason || addToRoster.isPending}
                        onClick={() => addToRoster.mutate({ employee_id: w.employee_id, worker_name: w.worker_name })}
                        style={{ ...t.bodySm, color: c.info, background: 'none', border: `1px solid ${c.border}`,
                                 borderRadius: r.md, padding: '4px 8px', cursor: 'pointer' }}
                      >Save</button>
                      <button
                        onClick={() => setAddingGapId(null)}
                        style={{ ...t.bodySm, color: c.textMuted, background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer' }}
                      >Cancel</button>
                    </div>
                  )}
                  {addToRoster.isError && addingGapId === w.employee_id && (
                    <div style={{ ...t.micro, color: c.dangerText, marginTop: 2 }}>
                      {addToRoster.error?.message || 'Could not add this worker.'}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canSeeAllocations && (
            <div style={{ marginTop: 10 }}>
              <button
                onClick={() => setShowDismissed((v) => !v)}
                style={{ ...t.micro, color: c.textMuted, background: 'none', border: 'none', padding: 0,
                         cursor: 'pointer', textDecoration: 'underline' }}
              >{showDismissed ? 'hide' : 'show'} dismissed names</button>
              {showDismissed && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {dismissals.isLoading && <li style={{ ...t.micro, color: c.textFaint }}>Loading…</li>}
                  {dismissals.data?.length === 0 && <li style={{ ...t.micro, color: c.textFaint }}>None dismissed.</li>}
                  {dismissals.data?.map((d) => (
                    <li key={d.employee_id} style={{ ...t.micro, color: c.textMuted, marginBottom: 3 }}>
                      {d.employee_id} — {d.reason}
                      {d.dismissed_by_name && ` (${d.dismissed_by_name})`}
                      <button
                        disabled={undoDismiss.isPending}
                        onClick={() => undoDismiss.mutate(d.employee_id)}
                        style={{ marginLeft: 8, ...t.micro, color: c.info, background: 'none', border: 'none',
                                 padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                      >undo</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'headcount' && <HeadcountTrend onSelectCostCenter={revealCostCenter} />}
      {tab === 'headcount' && <CostCenterTrend kind="headcount" onSelectCostCenter={revealCostCenter} />}

      {active.isLoading && <LoadingSpinner />}
      {active.error && (
        <div style={{ ...card.base, padding: 16, background: c.brandTint, color: c.dangerText, ...t.bodySm }}>
          {active.error.message || 'Could not load this view.'}
        </div>
      )}

      {active.data && tab === 'allocations' && <AllocationsTab data={active.data} />}

      {active.data && tab === 'headcount' && (
        <HeadcountTab data={active.data} expanded={expanded} onToggle={toggleExpanded} />
      )}

      {active.data && tab === 'positions' && (
        <PositionsTab data={active.data}
                      onMonthChange={setPositionMonth}
                      costCenter={positionCostCenter} onCostCenterChange={setPositionCostCenter} />
      )}

    </div>
  );
}
